import RBush from "rbush"
import { Point, Port } from ".."
import { Barrier } from "./Barrier"
import { produce } from "immer"

export { Geometry }

class Geometry {

  static dot(a: [number, number], b: [number, number]): number {
    return a[0] * b[0] + a[1] * b[1]
  }

  static leftOrthogonal(v: [number, number]): [number, number] {
    return [v[1] !== 0 ? -v[1] : 0, v[0]]
  }

  static distancePointSegment(point: [number, number], segmentPoint1: [number, number], segmentPoint2: [number, number]): number {
    const [px, py] = point,
      [s1x, s1y] = segmentPoint1,
      [s2x, s2y] = segmentPoint2
    const sxd = s2x - s1x,
      syd = s2y - s1y,
      sds = sxd * sxd + syd * syd

    let distance: number

    if(sds === 0) {
      const pxd = px - s1x,
        pyd = py - s1y

      distance = pxd * pxd + pyd * pyd
    } else {
      const t = ((px - s1x) * sxd + (py - s1y) * syd) / sds,
        ct = Math.max(0, Math.min(t, 1)),
        cpx = s1x + ct * sxd,
        cpy = s1y + ct * syd,
        fxd = px - cpx,
        fyd = py - cpy

      distance = fxd * fxd + fyd * fyd
    }

    return Math.sqrt(distance)
  }

  static closestPointOnSegment(point: [number, number], segmentPoint1: [number, number], segmentPoint2: [number, number]): [number, number] {
    const [px, py] = point,
      [s1x, s1y] = segmentPoint1,
      [s2x, s2y] = segmentPoint2
    const sxd = s2x - s1x,
      syd = s2y - s1y,
      sds = sxd * sxd + syd * syd

    if(sds === 0) {
      return [s1x, s1y]
    } else {
      const t = ((px - s1x) * sxd + (py - s1y) * syd) / sds,
        ct = Math.max(0, Math.min(t, 1)),
        cpx = s1x + ct * sxd,
        cpy = s1y + ct * syd

      return [cpx, cpy]
    }
  }

  static boundingBox(points: [number, number][]): {
    minX: number,
    maxX: number,
    minY: number,
    maxY: number
  } {
    const xs = points.map(p => p[0]),
    ys = points.map(p => p[1])

    return {
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
      minY: Math.min(...ys),
      maxY: Math.max(...ys)
    }
  }

  static polygonIsClockwise(points: [number, number][]): boolean {
    if(points.length < 3) {
      throw new Error('Unexpected input')
    }

    return points.reduce((accumulator: [[number, number], number], currentValue) => {
      accumulator[1] += (currentValue[0] - accumulator[0][0]) * (currentValue[1] + accumulator[0][1])
      accumulator[0] = currentValue
      return accumulator
    }, [points[points.length - 1], 0])[1] > 0
  }

  static findMovedPort(port: Port, point: Point, radius: number, barriers: RBush<Barrier>): Port {
    const candidates = barriers.search({ minX: point[0] - radius, minY: point[1] - radius, maxX: point[0] + radius, maxY: point[1] + radius })
    const distances = candidates.map(c => { return { distance: Geometry.distancePointSegment(point, c.points[0], c.points[1]), barrier: c } })
    distances.sort((a, b) => {
      if(a.distance !== b.distance) {
        return a.distance - b.distance
      } else {
        return Geometry.dot(a.barrier.normal, [point[0] - a.barrier.points[0][0], point[1] - a.barrier.points[0][1]]) >= 0 ? -1 : 1
      }
    })

    if(distances.length > 0 && distances[0].distance <= radius) {
      const base = Geometry.closestPointOnSegment(point, distances[0].barrier.points[0], distances[0].barrier.points[1])
      return produce(port, (draft: Port) => {
        draft.position = base
        draft.direction = distances[0].barrier.normal
      })
    } else {
      return produce(port, (draft: Port) => {
        draft.position = point
      })
    }
  }
}
